import type { AdminResourceItemDef } from "./resource-catalog.types";

export const ADMIN_RESOURCE_CATALOG_SYSTEM_INTEGRATIONS: readonly AdminResourceItemDef[] = [
  {
    id: "twilio-console",
    name: "Twilio Console",
    description:
      "Operator console over the whole Twilio fleet — master account and every subaccount — for numbers, compliance, messaging, voice and spend",
    category: "System",
    actions: [
      {
        id: "overview",
        name: "View Fleet Overview",
        description: "Read the cross-subaccount summary the console opens on"
      }
    ],
    blocks: [
      {
        id: "numbers",
        name: "Phone Numbers",
        description: "Search, buy, configure and release numbers on any account",
        actions: [
          { id: "list", name: "List Numbers", description: "View numbers across the fleet" },
          {
            id: "search",
            name: "Search Available Numbers",
            description: "Query Twilio inventory for purchasable numbers"
          },
          { id: "buy", name: "Buy Number", description: "Purchase a number onto an account" },
          {
            id: "configure",
            name: "Configure Number",
            description: "Set voice, SMS and status-callback webhooks"
          },
          {
            id: "transfer",
            name: "Transfer Number",
            description: "Move a number between the master account and a subaccount"
          },
          {
            id: "release",
            name: "Release Number",
            description: "Release a number back to Twilio and stop its billing"
          }
        ]
      },
      {
        id: "compliance",
        name: "Regulatory Compliance",
        description: "Regulatory bundles and addresses, per subaccount",
        actions: [
          {
            id: "list-bundles",
            name: "List Bundles",
            description: "Enumerate regulatory bundles without knowing their SIDs"
          },
          {
            id: "list-addresses",
            name: "List Addresses",
            description: "View regulatory addresses",
            unserved:
              "No endpoint serves it. `list-bundles` above is the only compliance action the " +
              "console has built; regulatory addresses are unimplemented. Delete this marker " +
              "when the route lands."
          },
          {
            id: "create-address",
            name: "Create Address",
            description: "Add a regulatory address to an account",
            unserved:
              "No endpoint serves it — regulatory addresses are unimplemented. Delete this " +
              "marker when the route lands."
          },
          {
            id: "delete-address",
            name: "Delete Address",
            description: "Remove a regulatory address",
            unserved:
              "No endpoint serves it — regulatory addresses are unimplemented. Delete this " +
              "marker when the route lands."
          },
          {
            id: "submit-bundle",
            name: "Submit Bundle",
            description: "Submit or resubmit a bundle for Twilio review",
            unserved:
              "No endpoint serves it. The console can list bundles and not submit one. " +
              "Delete this marker when the route lands."
          }
        ]
      },
      {
        id: "messaging",
        name: "Messaging",
        description: "Message log across the fleet, with Twilio error codes",
        actions: [
          {
            id: "list-messages",
            name: "List Messages",
            description: "Read inbound and outbound messages with their delivery status"
          }
        ]
      },
      {
        id: "whatsapp",
        name: "WhatsApp",
        description: "WhatsApp senders, templates and template approvals",
        actions: [
          {
            id: "list-senders",
            name: "List Senders",
            description: "View senders and the webhook each is bound to"
          },
          {
            id: "update-sender",
            name: "Update Sender",
            description: "Edit a sender profile or re-point its webhook"
          },
          {
            id: "delete-sender",
            name: "Delete Sender",
            description: "Remove a WhatsApp sender",
            unserved:
              "No endpoint serves it. Six sibling WhatsApp actions are guarded at their own " +
              "paths and sender deletion is unimplemented. Delete this marker when the route lands."
          },
          { id: "list-templates", name: "List Templates", description: "View message templates" },
          { id: "create-template", name: "Create Template", description: "Add a message template" },
          {
            id: "delete-template",
            name: "Delete Template",
            description: "Remove a message template"
          },
          {
            id: "submit-approval",
            name: "Submit Template Approval",
            description: "Send a template to Meta for approval"
          }
        ]
      },
      {
        id: "voice",
        name: "Voice",
        description: "Call log and call recordings",
        unserved:
          "No endpoint serves this block or either action below — the console's voice section " +
          "is unbuilt. Its siblings `numbers`, `compliance`, `messaging` and `whatsapp` are all " +
          "guarded at their own paths. Delete this marker when the routes land.",
        actions: [
          { id: "list-calls", name: "List Calls", description: "View calls across the fleet" },
          {
            id: "download-recording",
            name: "Download Recording",
            description: "Fetch a call recording"
          }
        ]
      },
      {
        id: "usage",
        name: "Usage and Spend",
        description: "Twilio usage and spend per subaccount",
        unserved:
          "No endpoint serves this block or the action below — usage and spend are unbuilt. " +
          "Delete this marker when the route lands.",
        actions: [
          {
            id: "view",
            name: "View Usage",
            description: "Read usage and spend figures for an account"
          }
        ]
      },
      {
        id: "errors",
        name: "Error Log",
        description: "Twilio Monitor alerts and debugger events, unified across subaccounts",
        unserved:
          "No endpoint serves this block or the action below — the unified error log is unbuilt. " +
          "Delete this marker when the route lands.",
        actions: [
          {
            id: "view",
            name: "View Error Log",
            description: "Read Twilio-side errors for any account in the fleet"
          }
        ]
      }
    ]
  },
  {
    id: "credentials-encryption",
    name: "Credentials Encryption",
    description:
      "Encrypt and rotate credentials at rest — ApiKeyConnection, ToolCredentials, UserCredential and OAuthConnection",
    category: "System",
    actions: [
      {
        id: "encrypt-all",
        name: "Encrypt All",
        description: "Encrypt plaintext credentials with active key",
        unserved:
          "Nothing reads this path. The encryption routes guard the item root " +
          "`admin:credentials-encryption` with the RWX bitmask, while the three backfill " +
          "actions below ARE guarded at their own paths — which is what makes this a gap."
      },
      {
        id: "rotate-key",
        name: "Rotate to New Key",
        description: "Re-encrypt all credentials with new encryption key",
        unserved:
          "Nothing reads this path. Key rotation is `admin:credentials-encryption` with the " +
          "write bit."
      },
      {
        id: "tool-credentials",
        name: "ToolCredentials Backfill",
        description:
          "Encrypt legacy plaintext ToolCredentials secrets (apikey, accessToken, refreshToken). " +
          "Grantable on its own so an operator can run the backfill without also holding " +
          "ApiKeyConnection key rotation; a grant on the parent still cascades to it."
      },
      {
        id: "user-credentials",
        name: "UserCredential Backfill",
        description:
          "Encrypt legacy plaintext UserCredential.key values — a customer's own OpenAI or " +
          "Lambda AI provider key. Grantable on its own so an operator can run the backfill " +
          "without also holding ApiKeyConnection key rotation; a grant on the parent still " +
          "cascades to it."
      },
      {
        id: "oauth-connections",
        name: "OAuthConnection Backfill",
        description:
          "Encrypt legacy plaintext OAuthConnection access and refresh tokens — live customer " +
          "grants for Gmail, Outlook, Slack and the rest. Every write is a compare-and-swap " +
          "against the value that was read, because a token-refresh cron writes the same " +
          "columns while the backfill runs. Grantable on its own so an operator can run the " +
          "backfill without also holding ApiKeyConnection key rotation; a grant on the parent " +
          "still cascades to it."
      }
    ]
  },
  {
    id: "ai-evaluation-budget",
    name: "AI Evaluation Budget",
    description: "Budget controls for AI workflow evaluation runs",
    category: "System"
  }
] as const;
